// src/pages/FAQPage.tsx
// Standalone FAQ pagina voor algemene WFT vragen
// SEO & GEO geoptimaliseerd met Google Analytics tracking
// Modern Glassmorphism Design - Fully Responsive

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';

// ============================================
// ANALYTICS TRACKING
// ============================================

// Voeg toe aan src/services/analytics.ts:
// export const trackFAQView = () => {
//   pushEvent('faq_page_view', {});
// };
// 
// export const trackFAQClick = (params: { question: string; category: string }) => {
//   pushEvent('faq_click', {
//     question: params.question.substring(0, 100),
//     category: params.category,
//   });
// };

const trackFAQClick = (question: string, category: string) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'faq_click',
      faq_question: question.substring(0, 100),
      faq_category: category,
    });
  }
};

const trackFAQPageView = () => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'faq_page_view',
    });
  }
};

// ============================================
// FAQ DATA - GEO Geoptimaliseerd
// ============================================

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  title: string;
  icon: string;
  faqs: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    id: 'algemeen',
    title: 'Algemene WFT Vragen',
    icon: '📋',
    faqs: [
      {
        question: 'Wat zijn WFT examens en waarom zijn ze verplicht?',
        answer: 'WFT examens zijn de officiële vakbekwaamheidsexamens voor financieel adviseurs in Nederland, verplicht gesteld door de Wet op het financieel toezicht (Wft). Deze wet schrijft voor dat iedereen die consumenten adviseert over financiële producten zoals verzekeringen, hypotheken, pensioenen of beleggingen, moet beschikken over de juiste WFT-diploma\'s. De examens worden afgenomen door CDFD-erkende exameninstituten en toetsen zowel theoretische kennis als praktische vaardigheden. Het doel is om consumenten te beschermen door te garanderen dat adviseurs voldoende kennis hebben om passend advies te geven. Zonder geldige WFT-certificering mag je in Nederland geen financieel advies geven aan particulieren.'
      },
      {
        question: 'Hoeveel WFT modules zijn er en welke heb ik nodig?',
        answer: 'Er zijn 9 WFT modules beschikbaar in Nederland. WFT Basis is verplicht voor iedereen in de financiële dienstverlening en vormt het fundament. Daarnaast zijn er specialistische modules: Schade Particulier (huishoudelijke verzekeringen), Schade Zakelijk (bedrijfsverzekeringen), Hypothecair Krediet, Vermogen (beleggingen), Inkomen (arbeidsongeschiktheid), Pensioen, Zorgverzekeringen, en Consumptief Krediet. Welke modules je nodig hebt, hangt af van je werkzaamheden. Een hypotheekadviseur heeft WFT Basis + Hypothecair Krediet nodig. Een verzekeringsadviseur voor particulieren heeft WFT Basis + Schade Particulier nodig. Je werkgever of de AFM kan aangeven welke beroepskwalificaties vereist zijn voor jouw functie.'
      },
      {
        question: 'Wat is de slaaggrens voor WFT examens?',
        answer: 'Voor alle WFT examens geldt een uniforme slaaggrens van 68% van het maximaal te behalen punten. Dit percentage is vastgesteld door het CDFD (College Deskundigheid Financiële Dienstverlening). Bij precies 68% krijg je een 5,5 dat wordt afgerond naar een 6, wat een voldoende is. Het aantal benodigde punten verschilt per examen omdat elk examen een ander maximaal puntenaantal heeft. Bijvoorbeeld: bij WFT Basis (40 punten max) heb je 27 punten nodig, bij WFT Schade Particulier (72 punten max) heb je 49 punten nodig. De examens kennen verschillende vraagtypen met verschillende puntenwaardes: Kennis & Begrip vragen (1 punt), Vaardigheden & Competenties (2 punten) en Professioneel Gedrag vragen (2 punten).'
      },
      {
        question: 'Hoeveel kost een WFT examen in 2025?',
        answer: 'De kosten voor WFT examens in 2025 liggen tussen €145 en €195 per examen, afhankelijk van het exameninstituut en de module. De meeste initiële examens kosten rond de €175. Bij een herkansing betaal je opnieuw het volledige examenbedrag. Naast de examenkosten moet je rekening houden met kosten voor studiemateriaal (€50-€200) en eventueel een opleiding (€200-€800). Sommige werkgevers vergoeden deze kosten. Het is financieel verstandig om je goed voor te bereiden, aangezien elke herkansing extra kosten met zich meebrengt. De exacte prijzen kun je vinden op de websites van erkende exameninstituten zoals DUO, NIBE-SVV, of andere CDFD-erkende instituten.'
      },
      {
        question: 'Hoe lang is een WFT diploma geldig?',
        answer: 'Een WFT diploma is in principe onbeperkt geldig, maar om je vakbekwaamheid te behouden moet je voldoen aan de Permanente Educatie (PE) verplichting. Dit betekent dat je elke drie jaar een PE-examen moet afleggen voor je hoogste beroepskwalificatie. Het PE-examen toetst of je kennis nog actueel is, gezien de voortdurende wijzigingen in wet- en regelgeving. Als je niet tijdig je PE-examen haalt, verlies je je adviesbevoegdheid en moet je opnieuw het initiële examen afleggen. De PE-periode loopt van 1 april tot 31 maart, drie jaar later. Je kunt je PE-status controleren via MijnWft.nl met je DigiD. Het is cruciaal om de PE-deadline niet te missen om je carrière in de financiële sector te kunnen voortzetten.'
      },
      {
        question: 'Waar kan ik een WFT examen afleggen?',
        answer: 'WFT examens worden afgenomen bij CDFD-erkende exameninstituten door heel Nederland. De grootste aanbieders zijn DUO (Dienst Uitvoering Onderwijs), NIBE-SVV, Lindenhaeghe, en ICM Opleidingen & Trainingen. Je kunt je online aanmelden via MijnWft.nl of direct bij het exameninstituut. De examens worden digitaal afgenomen op een computer in een gecontroleerde omgeving. Je moet je legitimeren met een geldig identiteitsbewijs en je BSN of Wft-ID meenemen. De meeste examenlocaties bevinden zich in grote steden, maar er zijn ook mogelijkheden in kleinere plaatsen. Controleer bij inschrijving welke locaties beschikbaar zijn en kies een datum en tijd die je uitkomt.'
      }
    ]
  },
  {
    id: 'examen',
    title: 'Examen & Voorbereiding',
    icon: '📝',
    faqs: [
      {
        question: 'Hoe lang moet ik studeren voor een WFT examen?',
        answer: 'De studietijd varieert sterk per module en je voorkennis. Voor WFT Basis is gemiddeld 40-60 uur nodig. Voor specialistische modules zoals Schade Particulier, Hypothecair Krediet of Vermogen moet je rekenen op 80-150 uur. Als je al werkzaam bent in de sector en dagelijks met de materie werkt, kun je mogelijk met minder uren toe. Zonder voorkennis is meer tijd realistischer. Het is aan te raden om de studie te spreiden over 6-12 weken, met gemiddeld 10-15 uur per week. Focus niet alleen op theorie, maar besteed minstens 30% van je tijd aan oefenvragen en proefexamens. Dit helpt je om de vraagstelling te leren kennen en je kennis toe te passen op praktijksituaties.'
      },
      {
        question: 'Wat is het verschil tussen KB, VC en PG vragen?',
        answer: 'WFT examens kennen drie vraagtypen. KB (Kennis & Begrip) vragen toetsen feitenkennis met korte, directe vragen over definities, regels en concepten. Deze leveren 1 punt op. VC (Vaardigheden & Competenties) vragen bevatten uitgebreide casussen waarbij je theoretische kennis moet toepassen op praktijksituaties. Je krijgt een beschrijving van een klantsituatie en moet analyseren welk advies passend is. Deze leveren 2 punten op. PG (Professioneel Gedrag) vragen gaan over ethische dilemma\'s, integriteit en professioneel handelen in lastige situaties. Ook deze leveren 2 punten op. De vragen worden in vaste volgorde aangeboden: eerst KB, dan PG, en tot slot VC. Omdat VC- en PG-vragen meer punten waard zijn, wegen fouten daar zwaarder.'
      },
      {
        question: 'Wat mag ik meenemen naar het WFT examen?',
        answer: 'Bij WFT examens mag je beperkt materiaal meenemen. Standaard toegestaan zijn: een niet-programmeerbare rekenmachine, schrijfmateriaal, en kladpapier (wordt vaak door het exameninstituut verstrekt). Bij examens met rekenkundige vragen zoals Hypothecair Krediet, Inkomen, Pensioen en Vermogen krijg je een fiscaal memo met actuele percentages en grenzen. Je mag geen eigen boeken, samenvattingen of aantekeningen meenemen. Elektronische apparaten zoals telefoons, smartwatches en tablets zijn verboden en moeten in een kluisje. Je moet je legitimeren met een geldig identiteitsbewijs (paspoort, ID-kaart of rijbewijs) en je BSN of Wft-ID bij je hebben. Controleer vooraf bij je exameninstituut welke specifieke regels gelden.'
      },
      {
        question: 'Wat gebeurt er als ik zak voor mijn WFT examen?',
        answer: 'Als je zakt voor een WFT examen, kun je het examen opnieuw afleggen. Er is geen wachttijd: je kunt direct een nieuwe examendatum plannen, afhankelijk van beschikbaarheid bij het exameninstituut. Je betaalt wel opnieuw het volledige examenbedrag (meestal €145-€195). Na je examen krijg je een uitdraai met je score per onderdeel, zodat je kunt zien waar je zwakke punten liggen. Gebruik deze feedback om gericht te studeren voor je herkansing. Er is geen maximum aantal pogingen, maar financieel en qua tijd is het natuurlijk verstandig om je goed voor te bereiden. Statistisch gezien slaagt 50-70% bij de eerste poging, afhankelijk van de module.'
      },
      {
        question: 'Hoe weet ik of ik klaar ben voor het examen?',
        answer: 'Je bent klaar voor het examen als je consistent 75% of hoger scoort op proefexamens onder examenomstandigheden (met tijdslimiet, zonder hulpmiddelen). Maak minimaal 3-5 volledige proefexamens en analyseer je fouten. Je moet niet alleen de theorie kennen, maar deze ook kunnen toepassen in casussen. Let op: veel kandidaten onderschatten de VC-vragen die veel leestijd kosten. Zorg dat je de belangrijkste concepten kunt uitleggen in je eigen woorden, niet alleen herkennen. Ken de exacte getallen (percentages, bedragen, termijnen) die vaak terugkomen. Als je bij proefexamens steeds dezelfde fouten maakt, besteed dan extra tijd aan die onderwerpen voordat je het echte examen plant.'
      }
    ]
  },
  {
    id: 'carriere',
    title: 'Carrière & Beroep',
    icon: '💼',
    faqs: [
      {
        question: 'Welke banen kan ik krijgen met een WFT diploma?',
        answer: 'Met WFT diploma\'s kun je werken als financieel adviseur in verschillende specialisaties. Met WFT Basis + Schade Particulier kun je werken als verzekeringsadviseur bij verzekeraars, banken, of onafhankelijke advieskantoren. Met Hypothecair Krediet kun je hypotheekadviseur worden. Met Vermogen kun je beleggingsadviseur worden bij banken of vermogensbeheerders. Met Inkomen en Pensioen kun je adviseren over arbeidsongeschiktheid en pensioenvoorzieningen. Typische werkgevers zijn: banken (Rabobank, ING, ABN AMRO), verzekeraars (Nationale-Nederlanden, Achmea, ASR), hypotheekkantoren, financieel adviesbureaus, en accountantskantoren. Je kunt ook als zelfstandig adviseur werken met een eigen vergunning van de AFM.'
      },
      {
        question: 'Hoeveel verdient een financieel adviseur?',
        answer: 'Het salaris van een financieel adviseur varieert sterk afhankelijk van ervaring, specialisatie en werkgever. Een starter verdient gemiddeld €2.500-€3.500 bruto per maand. Met enkele jaren ervaring stijgt dit naar €3.500-€5.000. Senior adviseurs en specialisten (bijvoorbeeld complexe hypotheken of vermogensbeheer) kunnen €5.000-€7.000 of meer verdienen. Bij banken en grote verzekeraars zijn de salarissen vaak hoger dan bij kleine advieskantoren, maar daar zijn weer meer doorgroeimogelijkheden. Daarnaast zijn er vaak bonussen gebaseerd op productie of klanttevredenheid. Zelfstandige adviseurs kunnen significant meer verdienen, maar hebben ook meer kosten en risico\'s. De financiële sector biedt over het algemeen goede arbeidsvoorwaarden en baanzekerheid.'
      },
      {
        question: 'Kan ik als zzp\'er werken met WFT diploma\'s?',
        answer: 'Ja, je kunt als zelfstandige financieel adviseur werken, maar dit vereist meer dan alleen WFT diploma\'s. Je hebt een vergunning van de AFM (Autoriteit Financiële Markten) nodig om advies te mogen geven. De vergunningsaanvraag kost rond de €4.000-€6.000 en je moet voldoen aan eisen voor bedrijfsvoering, klachtenafhandeling en een beroepsaansprakelijkheidsverzekering. Daarnaast moet je voldoende vakbekwame medewerkers hebben (vaak minimaal twee personen met de juiste diploma\'s). Veel zzp\'ers kiezen ervoor om te werken onder de vergunning van een aangesloten kantoor of franchise, waardoor ze geen eigen vergunning nodig hebben maar wel een deel van de provisie afstaan. Dit is een laagdrempeliger instap in het zelfstandig ondernemerschap.'
      },
      {
        question: 'Is er vraag naar financieel adviseurs op de arbeidsmarkt?',
        answer: 'Ja, er is structureel vraag naar gekwalificeerde financieel adviseurs. De financiële sector vergrijst, waardoor er veel adviseurs met pensioen gaan. Tegelijkertijd worden de eisen strenger en moet iedereen vakbekwaam zijn, wat de vraag naar gekwalificeerd personeel vergroot. Vooral in hypotheken en complexe verzekeringen is er tekort aan adviseurs. De digitalisering verandert wel de aard van het werk: routinematige producten worden online verkocht (execution only), maar voor complexe situaties blijft persoonlijk advies nodig. Adviseurs die zich specialiseren in niches (bijv. expats, ondernemers, medische beroepen) of die goed zijn in relatiebeheer hebben de beste vooruitzichten. Het is een stabiele sector met goede carrièreperspectieven voor wie zich blijft ontwikkelen.'
      }
    ]
  },
  {
    id: 'studybuddy',
    title: 'Over WFT Study Buddy',
    icon: '🎓',
    faqs: [
      {
        question: 'Wat is WFT Study Buddy?',
        answer: 'WFT Study Buddy is een AI-powered online platform voor WFT examenvoorbereiding. Wij bieden oefenvragen die zijn gebaseerd op de officiële CDFD toetstermen, complete proefexamens die het echte examen simuleren, en samenvattingen per module. Onze vragen worden gegenereerd en gevalideerd met behulp van kunstmatige intelligentie, waardoor we actuele en gevarieerde content kunnen bieden. Je kunt oefenen op laptop, tablet of telefoon, en je voortgang wordt bijgehouden zodat je kunt zien waar je sterke en zwakke punten liggen. We ondersteunen momenteel Schade Particulier en Schade Zakelijk volledig, met andere modules in ontwikkeling.'
      },
      {
        question: 'Is WFT Study Buddy gratis?',
        answer: 'WFT Study Buddy heeft een gratis tier waarmee je kunt kennismaken met het platform. Je krijgt toegang tot een beperkt aantal oefenvragen per dag en kunt proefexamens proberen. Voor onbeperkt oefenen en toegang tot alle features bieden we betaalde opties: je kunt proefexamens los kopen voor €4,99 per stuk, of een Pro-abonnement nemen voor €19,99 per maand met onbeperkte toegang tot alles. Het Pro-abonnement is ideaal als je intensief wilt studeren voor je examen. We accepteren iDEAL en creditcard betalingen. Er is geen langetermijnverplichting: je kunt je abonnement maandelijks opzeggen.'
      },
      {
        question: 'Hoe actueel zijn de oefenvragen?',
        answer: 'Onze oefenvragen zijn gebaseerd op de meest recente CDFD toetstermen en worden regelmatig geüpdatet wanneer de exameneisen veranderen. Het CDFD publiceert jaarlijks updates van de toetstermen, meestal per 1 april. Wij verwerken deze updates zo snel mogelijk in onze vragenbank. Daarnaast gebruiken we AI-validatie om te controleren of vragen nog actueel en correct zijn. Omdat wet- en regelgeving in de financiële sector regelmatig wijzigt (denk aan nieuwe belastingpercentages, grensbedragen, of wetgeving), is het belangrijk om met actueel materiaal te studeren. Wij vermelden bij onze content op welke versie van de toetstermen deze is gebaseerd.'
      },
      {
        question: 'Vervangt WFT Study Buddy een opleiding?',
        answer: 'WFT Study Buddy is primair een oefentool en vervangt geen complete opleiding. Voor de theorie raden we aan om studieboeken te gebruiken van erkende uitgevers of een cursus te volgen bij een opleidingsinstituut. Waar wij uitblinken is in het oefenen: met onze AI-gegenereerde vragen kun je veel meer variatie oefenen dan met traditionele vragenboeken. De combinatie van theoriestudie (boek of cursus) plus intensief oefenen (WFT Study Buddy) geeft de beste resultaten. Onze samenvattingen kunnen helpen als naslagwerk, maar dekken niet altijd alle stof even diepgaand. Zie ons als aanvulling op je studie, niet als vervanging.'
      }
    ]
  }
];

// Flatten all FAQs for SEO schema
const allFaqs: FAQItem[] = faqCategories.flatMap(cat => cat.faqs);

// ============================================
// ICONS
// ============================================

const ChevronDownIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// ============================================
// FAQ ITEM COMPONENT
// ============================================

interface FAQItemProps {
  faq: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  category: string;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ faq, isOpen, onToggle, category }) => {
  const handleClick = () => {
    if (!isOpen) {
      trackFAQClick(faq.question, category);
    }
    onToggle();
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
      <button
        onClick={handleClick}
        className="w-full flex items-center justify-between p-5 md:p-6 text-left hover:bg-gray-50/50 transition-colors"
        aria-expanded={isOpen}
      >
        <h3 className="text-base md:text-lg font-semibold text-gray-900 pr-4 speakable">
          {faq.question}
        </h3>
        <div className={`flex-shrink-0 transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDownIcon />
        </div>
      </button>
      
      <div 
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-5 md:px-6 pb-5 md:pb-6">
          <p className="text-gray-600 leading-relaxed speakable">
            {faq.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN FAQ PAGE COMPONENT
// ============================================

const FAQPage: React.FC = () => {
  const [openFaqs, setOpenFaqs] = useState<Set<string>>(new Set(['algemeen-0']));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Track page view on mount
  useEffect(() => {
    trackFAQPageView();
  }, []);

  // SEO Hook
  useSEO({
    title: 'Veelgestelde Vragen over WFT Examens | FAQ | WFT Study Buddy',
    description: 'Antwoorden op veelgestelde vragen over WFT examens: kosten, slaagpercentages, studietips, diploma geldigheid, carrièremogelijkheden en meer. Alles wat je moet weten over WFT certificering.',
    canonicalUrl: '/faq',
    faqs: allFaqs
  });

  // Toggle FAQ open/closed
  const toggleFaq = (categoryId: string, index: number) => {
    const key = `${categoryId}-${index}`;
    setOpenFaqs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Filter FAQs based on search
  const filteredCategories = searchQuery
    ? faqCategories.map(cat => ({
        ...cat,
        faqs: cat.faqs.filter(
          faq =>
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.faqs.length > 0)
    : faqCategories;

  // Filter by active category
  const displayCategories = activeCategory
    ? filteredCategories.filter(cat => cat.id === activeCategory)
    : filteredCategories;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <span className="text-2xl">📚</span>
              <span className="font-bold text-xl text-gray-900">WFT Study Buddy</span>
            </Link>
            
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/wft-examens" className="text-gray-600 hover:text-gray-900 transition font-medium">
                Alle Examens
              </Link>
              <Link to="/faq" className="text-blue-600 font-medium">
                FAQ
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900 transition font-medium">
                Contact
              </Link>
              <Link 
                to="/"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                Start Gratis
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-12 pb-8 md:pt-16 md:pb-12 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 speakable">
            Veelgestelde Vragen
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto speakable">
            Alles wat je moet weten over WFT examens, voorbereiding en carrière in de financiële sector.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Zoek in veelgestelde vragen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
            />
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-4 md:py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 md:gap-3 justify-center">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                activeCategory === null
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white/80 text-gray-600 hover:bg-gray-100'
              }`}
            >
              Alle categorieën
            </button>
            {faqCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  activeCategory === cat.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white/80 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span>{cat.icon}</span>
                <span className="hidden sm:inline">{cat.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {displayCategories.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                Geen resultaten gevonden voor "{searchQuery}"
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Wis zoekopdracht
              </button>
            </div>
          ) : (
            <div className="space-y-10">
              {displayCategories.map(category => (
                <div key={category.id}>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl">{category.icon}</span>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 speakable">
                      {category.title}
                    </h2>
                  </div>
                  
                  <div className="space-y-3">
                    {category.faqs.map((faq, index) => (
                      <FAQItemComponent
                        key={index}
                        faq={faq}
                        isOpen={openFaqs.has(`${category.id}-${index}`)}
                        onToggle={() => toggleFaq(category.id, index)}
                        category={category.title}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-center text-white shadow-xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Klaar om te beginnen met oefenen?
            </h2>
            <p className="text-blue-100 mb-8 text-lg">
              Start vandaag nog gratis met WFT oefenvragen en proefexamens.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                Start Gratis →
              </Link>
              <Link
                to="/wft-examens"
                className="bg-blue-500/30 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-blue-500/50 transition-all duration-300 border border-white/30"
              >
                Bekijk Alle Examens
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 md:py-12 bg-white/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Populaire WFT Examens
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'WFT Basis', slug: '/wft-basis', icon: '📋' },
              { name: 'Schade Particulier', slug: '/wft-schade-particulier', icon: '🏠' },
              { name: 'Hypotheken', slug: '/wft-hypotheken', icon: '🏦' },
              { name: 'Vermogen', slug: '/wft-vermogen', icon: '📈' },
            ].map(exam => (
              <Link
                key={exam.slug}
                to={exam.slug}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <span className="text-2xl">{exam.icon}</span>
                <span className="font-medium text-gray-900">{exam.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-2xl">📚</span>
                <span className="font-bold text-xl">WFT Study Buddy</span>
              </div>
              <p className="text-gray-400 text-sm">
                AI-powered WFT examenvoorbereiding voor financieel adviseurs in Nederland.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Examens</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/wft-basis" className="hover:text-white transition">WFT Basis</Link></li>
                <li><Link to="/wft-schade-particulier" className="hover:text-white transition">Schade Particulier</Link></li>
                <li><Link to="/wft-hypotheken" className="hover:text-white transition">Hypotheken</Link></li>
                <li><Link to="/wft-examens" className="hover:text-white transition">Alle Examens →</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/faq" className="hover:text-white transition">Veelgestelde Vragen</Link></li>
                <li><Link to="/contact" className="hover:text-white transition">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Juridisch</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition">Algemene Voorwaarden</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-white transition">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>© <span suppressHydrationWarning>{new Date().getFullYear()}</span>WFT Study Buddy. Alle rechten voorbehouden.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;